import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  unsafeCSS,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'heigh-offset',
    },
    _height: {
      state: true,
    },
    lockScroll: {
      type: Boolean,
      attribute: 'lock-scroll',
    },
    zIndex: {
      type: Number,
      attribute: 'z-index'
    }
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.lockScroll = false
    this.heightOffset = 3
    this.zIndex = '100'
    this.breakpoint = '(min-width: 768px)'
    this.bodyListener = this.bodyListener.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
  }

  requestUpdate(name, value) {
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
      return true
    }

    e && e.preventDefault()

    this.open = typeof value === 'undefined' ? !this.open : value
    this[`${this.open ? 'add' : 'remove'}BodyListener`]()
    this.open && this.lockHeight && this.setHeight()
    this.open && this.broadcast()
  }

  closeFromDetails(e) {
    var path = (e.composedPath && e.composedPath()) || e.path
    if (
      path && path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      if (e.target.closest('hh-details-modal').querySelector('details').hasAttribute('open')) {
        e.target.closest('hh-details-modal').querySelector('details').removeAttribute('open');
      }
      this.toggle()
      document.querySelector('.mobile-menu__icon #reverse')?.beginElement()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  addBodyListener() {
    if (this.lockScroll) {
      document.body.classList.add('overflow-hidden')
    }
  }

  removeBodyListener() {
    if (this.lockScroll) {
      document.body.classList.remove('overflow-hidden')
    }
  }

  bodyListener(e) {
    !e._intercepted && this.toggle()
  }

  setHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100vh - ${Math.floor(
        parentTop + detailsTop - this.heightOffset
      )}px)`
      : ''
  }

  broadcast() {
    this.dispatchEvent(
      new CustomEvent('modalOpened', {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <style>
        :host {
          z-index: var(--hh-z-index, ${unsafeCSS(this.zIndex)});
        }
      </style>
      <details
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        ${ref(this.$container)}
      >
        <summary
          @keyup="${this.toggle}"
          aria-expanded="${this.open}"
          aria-haspopup="dialog"
          role="button"
          ${ref(this.$container)}
        >
          <slot
            @click="${this.toggle}"
            class="${classMap({ 'is-open': this.open })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </summary>
      </details>
      <div
        class="content"
        role="dialog"
        aria-modal="true"
        aria-label="${this.label}"
        style="${styleMap({ height: this._height })}"
        ${ref(this.$details)}
      >
        <hh-focus-trap active="${this.open}">
          <slot
            name="details"
            @keyup="${this.closeWithEscape}"
            @click="${this.closeFromDetails}"
          >
        </hh-focus-trap>
      </div>`
  }

  static get styles() {
    return css`
      summary {
        list-style-type: none;
      }

      summary:focus {
        outline: 0.2rem solid var(--hh-color-focus);
        outline-offset: var(--hh-focus-outline-offset);
      }

      summary::-webkit-details-marker {
        display: none;
      }

      .content {
        pointer-events: none;
        position: var(--hh-position, absolute);
        top: var(--hh-top);
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--hh-modal-background);
        height: var(--hh-modal-height);
        z-index: var(--hh-z-index-inner, 1);
      }

      details[open] + .content {
        pointer-events: unset;
      }

      ::slotted([slot='details']) {
        width: 100%;
        display: flex;
      }
    `
  }
}

customElements.define('hh-details-modal', HHDetailsModal)
